import { postJson } from "@/domain/common/client/ApiClient"
import { toYearDay, toYearDayTime } from "@/domain/common/utils/DateUtils"
import { tournamentStatusLabel } from "@/domain/tournament/model/Tournament"
import { PagingRequestPayload } from "@bracket/idl/main/common/model/PagingPt"
import { fightSportPayloadFromJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import {
  TournamentStatusPayload,
  tournamentStatusPayloadFromJSON,
} from "@bracket/idl/main/tournament/model/TournamentPt"
import {
  TournamentGetByIdRequestPayload,
  TournamentGetByIdResponsePayload,
  TournamentGetByPagingRequestPayload,
  TournamentGetByPagingResponsePayload,
} from "@bracket/idl/main/tournament/payload/TournamentPayloadPt"
import type {
  TournamentPayload,
  TournamentSummaryPayload,
} from "@bracket/idl/main/tournament/model/TournamentPt"
import type {
  TournamentDetail,
  TournamentListItem,
} from "@/domain/tournament/model/Tournament"

interface TournamentSearchParam {
  name?: string
  status?: string
  sport?: string
  skip?: number
  take?: number
}

/** 시작·종료가 같은 날이면 하루로, 다르면 기간으로 적는다 */
function toPeriodText(startAt: string, endAt: string): string {
  const start = toYearDay(startAt)
  const end = toYearDay(endAt)

  if (!start) return ""
  if (!end || start === end) return start

  return `${start} ~ ${end}`
}

function toDetail(payload: TournamentPayload): TournamentDetail {
  return {
    id: payload.id,
    name: payload.name,
    periodText: toPeriodText(payload.startAt, payload.endAt),
    venue: payload.venue,
    startText: toYearDay(payload.startAt),
    entryOpenText: toYearDayTime(payload.entryOpenAt),
    entryCloseText: toYearDayTime(payload.entryCloseAt),
    sports: payload.sport,
    status: payload.status,
    statusLabel: tournamentStatusLabel(payload.status),
    isEntryOpen:
      payload.status === TournamentStatusPayload.TOURNAMENT_ENTRY_OPEN,
    notice: payload.notice,
  }
}

function toListItem(payload: TournamentSummaryPayload): TournamentListItem {
  return {
    id: payload.id,
    name: payload.name,
    venue: payload.venue,
    periodText: toPeriodText(payload.startAt, payload.endAt),
    startText: toYearDay(payload.startAt),
    entryCloseText: toYearDayTime(payload.entryCloseAt),
    status: payload.status,
    statusLabel: tournamentStatusLabel(payload.status),
    isEntryOpen:
      payload.status === TournamentStatusPayload.TOURNAMENT_ENTRY_OPEN,
    sports: payload.sport,
    entryCount: payload.entryCount,
  }
}

function toPagingPayload(param: TournamentSearchParam): PagingRequestPayload {
  return PagingRequestPayload.create({
    skip: param.skip ?? 0,
    take: param.take ?? 100,
  })
}

function toSearchPayload(param: TournamentSearchParam) {
  return {
    name: param.name ?? "",
    status: tournamentStatusPayloadFromJSON(
      param.status || "TOURNAMENT_STATUS_UNKNOWN",
    ),
    sport: fightSportPayloadFromJSON(param.sport || "FIGHT_SPORT_UNKNOWN"),
  }
}

/**
 * 대회 도메인 클라이언트.
 *
 * 경로는 fight-bracket-server 의 `TournamentRouter`(공개) · `TournamentAdminRouter`(관리자)와
 * 1:1 대응한다. **모든 엔드포인트는 POST + protobuf JSON** 이다.
 */
const TournamentClient = {
  /** 대회 상세 — 신청 폼 · 홈 · 상세 화면이 같은 값을 본다 */
  async getById(id: string): Promise<TournamentDetail> {
    const request = TournamentGetByIdRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/tournament/get-by-id",
      TournamentGetByIdRequestPayload.toJSON(request),
    )
    const response = TournamentGetByIdResponsePayload.fromJSON(json)

    if (!response.data) throw new Error("대회를 찾을 수 없습니다.")

    return toDetail(response.data)
  },

  /** 공개 대회 목록 — 취소된 대회는 서버가 빼고 내려준다 */
  async getByPaging(
    param: TournamentSearchParam = {},
  ): Promise<TournamentListItem[]> {
    const request = TournamentGetByPagingRequestPayload.create({
      paging: toPagingPayload(param),
      search: toSearchPayload(param),
    })

    const json = await postJson<unknown>(
      "/tournament/get-by-paging",
      TournamentGetByPagingRequestPayload.toJSON(request),
    )
    const response = TournamentGetByPagingResponsePayload.fromJSON(json)

    return response.data.map(toListItem)
  },
}

export {
  TournamentClient,
  toDetail,
  toListItem,
  toPagingPayload,
  toSearchPayload,
}
export type { TournamentSearchParam }
